// Aligné sur le `@Email` (Hibernate Validator) du backend : chaque étiquette de
// domaine doit contenir au moins un caractère, ce qui exclut le point double
// (`nom@gmail..com`) et les points en tête/fin — que le motif naïf
// `^[^\s@]+@[^\s@]+\.[^\s@]+$` laissait passer, pour un refus serveur ensuite.
const EMAIL_LOCAL = "[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*";
const EMAIL_LABEL = "[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?";
export const EMAIL_RE = new RegExp(`^${EMAIL_LOCAL}@(?:${EMAIL_LABEL}\\.)+[A-Za-z]{2,}$`);
// Permissif (CI/international) : au moins 8 chiffres, '+' et séparateurs tolérés.
export const PHONE_RE = /^\+?\d(?:[\d\s.-]*\d){7,}$/;

export function isValidEmail(v: string): boolean { return EMAIL_RE.test(v.trim()); }
export function isValidPhone(v: string): boolean { return PHONE_RE.test(v.trim()); }
export function isValidMontant(v: number | null | undefined): boolean { return v != null && v > 0; }
// Pas de contrainte de FORMAT sur le matricule : aucun format SEDEC canonique
// n'est connu et il peut varier d'un établissement à l'autre. On exige seulement
// une valeur non vide et bornée ; l'école identifie l'élève par ce matricule.
export function isValidMatricule(v: string): boolean {
  const t = v.trim();
  return t.length >= 2 && t.length <= 64;
}

export interface PayerFormState {
  ecoleId: string | null;
  motifId: string | null;
  montantXof: number | null;
  methode: string | null;
  eleveMode: "matricule" | "manual";
  matricule: string;
  eleve: { nom: string; prenoms: string; dateNaissance: string };
  payer: { email: string; telephone: string; prenoms: string; nom: string };
}

/** Champs communs aux deux parcours (en ligne + hors système), hors méthode. */
function firstSharedError(s: PayerFormState): string | null {
  if (!s.ecoleId) return "Sélectionnez une école depuis l'annuaire ou le tableau de bord.";
  if (!s.motifId) return "Choisissez un motif de paiement.";
  // Le montant n'est plus saisi ni fixé par le motif (22/10/2026) : c'est le
  // reste exact de la tranche ou du motif choisi sous « Ce que vous réglez ».
  if (!isValidMontant(s.montantXof)) return "Choisissez le montant à régler : une tranche ou le reste du motif.";
  if (s.eleveMode === "matricule") {
    if (!isValidMatricule(s.matricule)) return "Renseignez le matricule de l'enfant.";
  } else {
    if (!s.eleve.prenoms.trim()) return "Renseignez les prénoms de l'enfant.";
    if (!s.eleve.nom.trim()) return "Renseignez le nom de l'enfant.";
    if (!s.eleve.dateNaissance) return "Renseignez la date de naissance de l'enfant.";
  }
  if (!s.payer.prenoms.trim()) return "Renseignez vos prénoms.";
  if (!s.payer.nom.trim()) return "Renseignez votre nom.";
  if (!isValidEmail(s.payer.email)) return "Adresse email invalide.";
  if (!isValidPhone(s.payer.telephone)) return "Numéro de téléphone invalide.";
  return null;
}

/** 1er message d'erreur (champ manquant/invalide), ou null si le formulaire est soumissible. */
export function firstPayerError(s: PayerFormState): string | null {
  const shared = firstSharedError(s);
  if (shared) return shared;
  if (!s.methode) return "Choisissez un mode de paiement.";
  return null;
}

/** Canaux de paiement hors système (doivent matcher le CHECK SQL côté backend). */
export const CANAUX_HORS_SYSTEME = [
  { value: "VIREMENT", label: "Virement bancaire" },
  { value: "DEPOT_BANQUE", label: "Dépôt en banque" },
  { value: "CHEQUE", label: "Chèque" },
  { value: "ESPECES", label: "Espèces (au guichet)" },
  { value: "AUTRE", label: "Autre" },
] as const;

const CANAUX_VALIDES = new Set<string>(CANAUX_HORS_SYSTEME.map((c) => c.value));

export interface HorsSystemeExtra {
  canal: string;
  datePaiement: string;
  hasFile: boolean;
}

/**
 * Reste dû d'une déclaration en attente (SCOM-04, 22/10/2026), affiché à
 * l'école avant de valider : celui de la tranche déclarée s'il y en a une,
 * sinon celui du motif. `null` quand le serveur ne le donne pas.
 */
export function resteDeclaration(d: {
  montantXof: number;
  motifEcheanceId?: string | null;
  resteMotifXof?: number | null;
  resteTrancheXof?: number | null;
}): { reste: number; solde: boolean; depasse: boolean } | null {
  const reste = d.motifEcheanceId ? d.resteTrancheXof : d.resteMotifXof;
  if (reste == null) return null;
  const solde = reste <= 0;
  return { reste, solde, depasse: !solde && d.montantXof > reste };
}

/** 1er message d'erreur pour la déclaration hors système, ou null si soumissible. */
export function firstHorsSystemeError(s: PayerFormState, x: HorsSystemeExtra): string | null {
  const shared = firstSharedError(s);
  if (shared) return shared;
  if (!CANAUX_VALIDES.has(x.canal)) return "Indiquez le moyen utilisé (virement, dépôt…).";
  if (!x.datePaiement) return "Indiquez la date du paiement.";
  if (!x.hasFile) return "Ajoutez une preuve (image JPEG/PNG ou PDF).";
  return null;
}
